import React, { useState, useEffect, forwardRef, useCallback } from 'react';
import { CheckboxButton } from "@/components/atoms/Checkbox";
import { NumericFormat } from 'react-number-format';
import CalculationArray from '@/types/AutoInvoice/CalculationArray';


type InvoiceTableProps = {
  data: CalculationArray;
  currency: string;
  unVisibleColumns: any[];
  setSubtotal: (subtotal: number) => void;
  setActualSubTotal: (actualSubAmount: number) => void;
  onColumnsChange: (unVisibleColumns: any[]) => void;
};

type tableAmounts = { [sowId: string]: { [key: string]: number } }

const getMonthAbbreviation = (servicePeriod: string) => {
  const monthMap: { [key: string]: string } = {
    '01': 'Jan',
    '02': 'Feb',
    '03': 'Mar',
    '04': 'Apr',
    '05': 'May',
    '06': 'Jun',
    '07': 'Jul',
    '08': 'Aug',
    '09': 'Sep',
    '10': 'Oct',
    '11': 'Nov',
    '12': 'Dec',
  };
  const year = servicePeriod.substring(0, 4);
  const month = servicePeriod.substring(4, 6);
  return `${monthMap[month] || ''}`;
};

const formatServicePeriods = (servicePeriods: string[]) => {
  const uniquePeriods = Array.from(new Set(servicePeriods));
  uniquePeriods.sort();

  const formattedPeriods = uniquePeriods.map((period) => getMonthAbbreviation(period));
  if (formattedPeriods.length === 1) {
    return formattedPeriods[0];
  } else {
    return `${formattedPeriods[0]} - ${formattedPeriods[formattedPeriods.length - 1]}`;
  }
};

function capitalizeFirstLetter(str : string) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

const InvoiceTable = forwardRef<HTMLDivElement, InvoiceTableProps>(
  ({ data, currency, unVisibleColumns, setSubtotal, setActualSubTotal, onColumnsChange }, ref) => {
    const allColumns = [
      { label: 'Sow ID', value: 'sowId' },
      { label: 'Service Period', value: 'servicePeriod' },
      { label: 'Service Type', value: 'serviceType' },
      { label: 'Rate', value: 'rate' },
      { label: 'Description', value: 'description' },
      { label: 'Hours', value: 'hours' },
      { label: 'Sub-Amount', value: 'subAmount' },
    ];
    const [tableRows, setTableRows] = useState<JSX.Element[]>([]);
    const [initialSubAmounts, setInitialSubAmounts] = useState<tableAmounts>({});
    const [subAmounts, setSubAmounts] = useState<tableAmounts>({});
    const [updatedAmounts, setUpdatedAmounts] = useState<{ [sowId: string]: number }>({});
    const [visibleColumns, setVisibleColumns] = useState<any[]>(allColumns.filter(column => !unVisibleColumns.includes(column.value)));

    const [actualSubAmount, setActualSubAmount] = useState<number>(0);
    const columnOptions = [
      { label: 'Sow ID', value: 'sowId' },
      { label: 'Service Period', value: 'servicePeriod' },
      { label: 'Service Type', value: 'serviceType' },
      { label: 'Rate', value: 'rate' },
      { label: 'Description', value: 'description' },
      { label: 'Hours', value: 'hours' },
      { label: 'Sub-Amount', value: 'subAmount' },
    ];

    const formatNumber = (num: number, addCurrency: boolean = true) => {
      num = parseFloat(num.toFixed(2));
      if (num === 0) return '';
      return `${addCurrency ? `${currency} ` : ''}${new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(num)}`;
    };

    const handleColumnVisibilityChange = (label: string, value: string, isChecked: boolean) => {
      let newColumns = [...visibleColumns];

      if (isChecked) {
        newColumns.push({ label: label, value: value });
        if (label === 'Service Type') {
          
          if (!newColumns.some(newCol => newCol.value === 'rate')) newColumns.push({ label: 'Rate', value: 'rate' });
          if (!newColumns.some(newCol => newCol.value === 'hours')) newColumns.push({ label: 'Hours', value: 'hours' });
          if (!newColumns.some(newCol => newCol.value === 'subAmount')) newColumns.push({ label: 'Sub-Amount', value: 'subAmount' });
        }
      } else {
        newColumns = newColumns.filter(column => column.label !== label);
        if (label === 'Service Type') {
          newColumns = newColumns.filter(column => !["Rate", "Hours", "Sub-Amount"].includes(column.label))
        }
      }

      setVisibleColumns(newColumns);
    };

    const handleSubAmountChange = (sowId: string, key: string, value: number) => {
      value = parseFloat(value.toFixed(2));
      setSubAmounts((prev) => {
        const updatedSow = {
          ...prev[sowId],
          [key]: value,
        };
      
        return {
          ...prev,
          [sowId]: updatedSow,
        };
      });
    };

    const handleAmountChange = (sowId: string, value: number) => {
      setUpdatedAmounts((prev) => ({
        ...prev,
        [sowId]: parseFloat(value.toFixed(2)),
      }));
    };

    const calculateTotalAmount = (sowId: string) => {
      if (updatedAmounts[sowId] !== undefined) {
        return updatedAmounts[sowId];
      }

      if (!subAmounts[sowId]) return 0;

      return Object.values(subAmounts[sowId]).reduce((acc, val) => parseFloat(acc.toFixed(2)) + parseFloat(val.toFixed(2)), 0);
    };
    
    useEffect(() => {
      const unVisibleColumns = allColumns.filter(column => !visibleColumns.some(unCol => unCol.value === column.value))

      onColumnsChange(unVisibleColumns);
    }, [visibleColumns]);

    useEffect(() => {
      // Initialize the initialSubAmounts state with values from the data
      const initialAmounts: { [sowId: string]: { [key: string]: number } } = {};

      Object.entries(data).forEach(([sowId, serviceTypes]) => {
        initialAmounts[sowId] = {};
        let mergedServiceTypes: Set<string> = new Set();
        let shouldMergeServiceTypes = false;
        let mergedSubAmount = 0;

        Object.entries(serviceTypes).forEach(([serviceType, rates]) => {
          const serviceTypeKey = `${serviceType}`;
          let serviceTypeSubAmount = 0;

          Object.values(rates).forEach((items) => {
            if (Array.isArray(items)) {
              if (items.some((item) => item.paymentType === "Fixed Fee" || item.paymentType === "Flexi")) {
                shouldMergeServiceTypes = true;
                mergedServiceTypes.add(serviceType);
                items.forEach((item) => {
                  mergedSubAmount += item.netRevenueLocal;
                });
              }
            }
          });

          if (shouldMergeServiceTypes) {
            shouldMergeServiceTypes = false;
          } else {
            Object.entries(rates).forEach(([rate, items]) => {
              items.forEach((item) => {
                if (item.serviceType === 'ZZZADDITIONAL COST') {
                  serviceTypeSubAmount += item.netRevenueLocal;
                } else if (item.serviceType === 'ZZZZDISCOUNT') {
                  serviceTypeSubAmount += item.discountLocal * -1;
                } else {
                  serviceTypeSubAmount += item.discountLocal !== 0 ? item.grossRevenueLocal : item.netRevenueLocal;
                }
              });
            });

            initialAmounts[sowId][serviceTypeKey] = serviceTypeSubAmount;
          }
        });

        if (mergedServiceTypes.size > 0) {
          const displayServiceTypes = [...mergedServiceTypes].filter(
            (serviceType) => serviceType !== "Unknown"
          );
          initialAmounts[sowId][displayServiceTypes.join(', ')] = mergedSubAmount;
        }
      });

      const actualTotalAmount = calculateActualTotalAmount(initialAmounts);
      setActualSubAmount(parseFloat(actualTotalAmount.toFixed(2)));
   
      setInitialSubAmounts(initialAmounts);
      setSubAmounts(initialAmounts); // Initialize subAmounts with initial values
    }, [data, visibleColumns]);

    const calculateActualTotalAmount = (dataObject: tableAmounts): number => {
      return Object.values(dataObject).reduce((total, innerObj) => {
        const innerSum = Object.values(innerObj).reduce(
          (sum, value) => sum + value,
          0
        );
        return total + innerSum;
      }, 0);
    };
    
    const renderTableRows = () => {
      let tableTotalAmount = 0;
      const rows: JSX.Element[] = [];

      const shouldCombineRows =
      !visibleColumns.some(column => column.label === 'Service Type') &&
      !visibleColumns.some(column => column.label === 'Sow ID') &&
      !visibleColumns.some(column => column.label === 'Service Period');

      if (shouldCombineRows) {
        // Calculate the combined total amount for all SOWs
        const combinedTotalAmount = Object.keys(data).reduce((total, sowId) => {
          return total + calculateTotalAmount(sowId);
        }, 0);
    
        // Render a single row with either Description and Amount or just Amount
        rows.push(
          <tr key="combined-row" className="border-b-2 border-border">
            {visibleColumns.some(column => column.label === 'Description') && (
              <td className="p-2 description">
                <input
                  type="text"
                  defaultValue=""
                  className="border p-1 w-full"
                  placeholder="Enter description"
                />
              </td>
            )}
            <td
              className="p-2 text-right totalAmount"
              colSpan={
                visibleColumns.some(column => column.label === 'Description') ? 1 : 2
              }
            >
              <NumericFormat
                value={combinedTotalAmount}
                displayType="input"
                thousandSeparator=","
                decimalSeparator="."
                fixedDecimalScale
                decimalScale={2}
                className="border p-1 w-full text-right"
                prefix={`${currency} `}
                onValueChange={(values) => {
                  const { value } = values;
                  setSubtotal(parseFloat(value));
                }}
              />
            </td>
          </tr>
        );
    
        // Return the single combined row
        return rows;
      }
    

      Object.entries(data).forEach(([sowId, serviceTypes], sowIndex) => {
        let sowRowSpanCount = 0;
        let sowRows: JSX.Element[] = [];
        let allServicePeriods: string[] = [];
        let totalAmount = 0;
        let shouldMergeServiceTypes = false;
        let mergedServiceTypes: Set<string> = new Set();
        let mergedSubAmount = 0;

        Object.entries(serviceTypes).forEach(([serviceType, rates], serviceTypeIndex) => {
          let serviceTypeRowSpanCount = 0;
          let serviceTypeRows: JSX.Element[] = [];

          Object.values(rates).forEach((items) => {
            if (Array.isArray(items)) {
              allServicePeriods.push(...items.map((item) => item.servicePeriod));

              if (items.some((item) => item.paymentType === "Fixed Fee" || item.paymentType === "Flexi")) {
                shouldMergeServiceTypes = true;
                mergedServiceTypes.add(serviceType);
                items.forEach((item) => {
                  mergedSubAmount += item.netRevenueLocal;
                });
              }
            }
          });

          if (shouldMergeServiceTypes) {
            shouldMergeServiceTypes = false;
          } else {
            if (visibleColumns.some(column => column.label === 'Rate')) {
              Object.entries(rates).forEach(([rate, items], rateIndex) => {
                if (Array.isArray(items)) {
                  allServicePeriods.push(...items.map((item) => item.servicePeriod));
                  const totalHours = items.reduce((sum, item) => sum + item.hour, 0);
                  let totalSubAmount = 0;

                  items.forEach((item) => {
                    if (item.serviceType === 'ZZZADDITIONAL COST') {
                      totalSubAmount += item.netRevenueLocal;
                    } else if (item.serviceType === 'ZZZZDISCOUNT') {
                      totalSubAmount += item.discountLocal * -1;
                    } else {
                      totalSubAmount += item.discountLocal !== 0 ? item.grossRevenueLocal : item.netRevenueLocal;
                    }
                  });

                  totalAmount += totalSubAmount;

                  const servicePeriod = getMonthAbbreviation(items[0].servicePeriod);
                  let senseServiceType = serviceType;
                  
                  if (senseServiceType == 'ZZZADDITIONAL COST') {
                    senseServiceType = 'Additional Cost';
                  } else if (senseServiceType == 'ZZZZDISCOUNT') {
                    senseServiceType = 'Discount';
                  }

                  serviceTypeRows.push(
                    <tr key={`${sowId}-${serviceType}-${rate}-${rateIndex}-${serviceTypeIndex}-${sowIndex}`} className="border-b-2 border-border">
                      {serviceTypeRowSpanCount === 0 && (
                        <>
                        {visibleColumns.some(column => column.label === 'Service Type') &&
                          <td className="p-2 serviceType" rowSpan={Object.keys(rates).length}>
                            <input
                              type="text"
                              defaultValue={capitalizeFirstLetter(senseServiceType)}
                              className="border p-1 w-full"
                            />
                          </td>
                        }
                      </>
                      
                      )}
                      {visibleColumns.some(column => column.label === 'Rate') && 
                        <td className="p-2 text-right rate">{formatNumber(parseFloat(rate))}</td>
                      }
                      {visibleColumns.some(column => column.label === 'Description') && (
                        <td className="p-2 description">
                          <input
                            type="text"
                            defaultValue=""
                            className="border p-1 w-full"
                            placeholder="Enter description"
                          />
                        </td>
                      )}
                      {visibleColumns.some(column => column.label === 'Hours') && (
                        <td className="p-2 text-right hours">{formatNumber(totalHours, false)}</td>
                      )}
                      {visibleColumns.some(column => column.label === 'Sub-Amount') && (
                        <td className="p-2 text-right subAmount">
                          {formatNumber(totalSubAmount)}
                        </td>
                      )}
                    </tr>
                  );
                  serviceTypeRowSpanCount++;
                }
              });
            } else {
              let mergedHours = 0;
              const serviceTypeKey = `${serviceType}`;
              const serviceTypeSubAmount = initialSubAmounts[sowId]?.[serviceTypeKey] ?? 0;


              Object.values(rates).forEach((items) => {
                if (Array.isArray(items)) {
                  allServicePeriods.push(...items.map((item) => item.servicePeriod));
                  mergedHours += items.reduce((sum, item) => sum + item.hour, 0);
                }
              }); 

              totalAmount += serviceTypeSubAmount;
              
              let senseServiceType = serviceType;

              if (senseServiceType == 'ZZZADDITIONAL COST') {
                senseServiceType = 'Additional Cost';
              } else if (senseServiceType == 'ZZZZDISCOUNT') {
                senseServiceType = 'Discount';
              }

              serviceTypeRows.push(
                <tr key={`${sowId}-${serviceType}-merged-${serviceTypeIndex}-${sowIndex}`} className="border-b-2 border-border">
                  {serviceTypeRowSpanCount === 0 && (
                    <>
                      {visibleColumns.some(column => column.label === 'Service Type') && (

                        <td
                          className="p-2 serviceType"
                          rowSpan={1}
                        >
                          <input
                            type="text"
                            defaultValue={capitalizeFirstLetter(senseServiceType)}
                            className="border p-1 w-full"
                          />
                        </td>
                      )}
                    </>
                  )}
                  {visibleColumns.some(column => column.label === 'Service Type') && visibleColumns.some(column => column.label === 'Description') && (
                    <td className="p-2 description">
                      <input
                        type="text"
                        defaultValue=""
                        className="border p-1 w-full"
                        placeholder="Enter description"
                      />
                    </td>
                  )}
                  {visibleColumns.some(column => column.label === 'Hours') && (
                    <td className="p-2 text-right hours">{formatNumber(mergedHours, false)}</td>
                  )}
                  {!visibleColumns.some(column => column.label === 'Rate') &&
                  !visibleColumns.some(column => column.label === 'Hours') &&
                  visibleColumns.some(column => column.label === 'Sub-Amount') ? (
                    <td className="p-2 text-right subAmount">
                      <NumericFormat
                        value={serviceTypeSubAmount}
                        displayType="input"
                        thousandSeparator=","
                        decimalSeparator="."
                        fixedDecimalScale
                        decimalScale={2}
                        className="border p-1 w-full text-right"
                        prefix={`${currency} `}
                        onValueChange={(values) => {
                          const { value } = values;
                          handleSubAmountChange(sowId, serviceTypeKey, parseFloat(value));
                        }}
                      />
                    </td>
                  ) : visibleColumns.some(column => column.label === 'Sub-Amount') ? (
                    <td className="p-2 text-right subAmount">{formatNumber(serviceTypeSubAmount)}</td>
                  ) : null}
                </tr>
              );
              serviceTypeRowSpanCount++;
            }
          }
          
          sowRows = sowRows.concat(serviceTypeRows);
          sowRowSpanCount += serviceTypeRowSpanCount;
        });

        if (mergedServiceTypes.size > 0) {
          const displayServiceTypes = [...mergedServiceTypes].filter(
            (serviceType) => serviceType !== "Unknown"
          );
          const serviceTypeSubAmount = initialSubAmounts[sowId]?.[displayServiceTypes.join(', ')] ?? 0;
          sowRows.push(
            <tr key={`${sowId}-${displayServiceTypes}-${sowIndex}`} className="border-b-2 border-border">
              {visibleColumns.some(column => column.label === 'Service Type') &&
                <td className="p-2 serviceType" rowSpan={1}>
                  <input
                    type="text"
                    defaultValue={capitalizeFirstLetter(displayServiceTypes.join(', '))}
                    className="border p-1 w-full"
                  />
                </td>
              }
              {visibleColumns.some(column => column.label === 'Rate')&& 
                <td className="p-2 text-right rate"></td>
              }
              {visibleColumns.some(column => column.label === 'Service Type') && visibleColumns.some(column => column.label === 'Description')&& (
                <td className="p-2 description">
                  <input
                    type="text"
                    defaultValue=""
                    className="border p-1 w-full"
                    placeholder="Enter description"
                  />
                </td>
              )}
              {visibleColumns.some(column => column.label === 'Hours')&& (
                <td className="p-2 text-right hours"></td>
              )}
              {!visibleColumns.some(column => column.label === 'Rate') &&
              !visibleColumns.some(column => column.label === 'Hours')&&
              visibleColumns.some(column => column.label === 'Sub-Amount') ? (
                <td className="p-2 text-right subAmount">
                  <NumericFormat
                    value={serviceTypeSubAmount}
                    displayType="input"
                    thousandSeparator=","
                    decimalSeparator="."
                    fixedDecimalScale
                    decimalScale={2}
                    className="border p-1 w-full text-right"
                    prefix={`${currency} `}
                    onValueChange={(values) => {
                      const { value } = values;
                      handleSubAmountChange(sowId, displayServiceTypes.join(', '), parseFloat(value));
                    }}
                  />
                </td>
              ) : visibleColumns.some(column => column.label === 'Sub-Amount') ? (
                <td className="p-2 text-right subAmount">{formatNumber(serviceTypeSubAmount)}</td>
              ) : null}
            </tr>
          );
          totalAmount = mergedSubAmount;
          sowRowSpanCount += 1;
        }

        if (sowRows.length > 0) {
          const formattedServicePeriods = formatServicePeriods(allServicePeriods);
          sowRows[0] = React.cloneElement(sowRows[0], {
            key: `sow-row-${sowId}`,
            children: (
              <>
                {visibleColumns.some(column => column.label === 'Sow ID')&& (
                  <td
                    className="p-2 sowId"
                    rowSpan={sowRowSpanCount}
                  >
                    {sowId}
                  </td>
                )}
                {visibleColumns.some(column => column.label === 'Service Period')&& (
                  <td
                    className="p-2 servicePeriod"
                    rowSpan={sowRowSpanCount}
                  >
                    {formattedServicePeriods}
                  </td>
                )}
                {sowRows[0].props.children}
                {!visibleColumns.some(column => column.label === 'Service Type') && visibleColumns.some(column => column.label === 'Description') && (
                  <td className="p-2 description">
                    <input
                      type="text"
                      defaultValue=""
                      className="border p-1 w-full"
                      placeholder="Enter description"
                    />
                  </td>
                )}
                {!visibleColumns.some(column => column.label === 'Rate')&&
                !visibleColumns.some(column => column.label === 'Hours')&&
                !visibleColumns.some(column => column.label === 'Sub-Amount') ? (
                  <td className="p-2 text-right totalAmount" rowSpan={sowRowSpanCount}>
                    <NumericFormat
                      value={calculateTotalAmount(sowId)}
                      displayType="input"
                      thousandSeparator=","
                      decimalSeparator="."
                      fixedDecimalScale
                      decimalScale={2}
                      className="border p-1 w-full text-right"
                      prefix={`${currency} `}
                      onValueChange={(values) => {
                        const { value } = values;
                        handleAmountChange(sowId, parseFloat(value));
                      }}
                    />
                  </td>
                ) : (
                  <td className="p-2 text-right totalAmount" rowSpan={sowRowSpanCount}>
                    {formatNumber(calculateTotalAmount(sowId))}
                  </td> 
                )}
                
              </>
            ),
          });
        }
      
        tableTotalAmount += totalAmount;
        rows.push(...sowRows);
      });

      return rows;
    };

    useEffect(() => {
      // Calculate the subtotal from all amounts, taking into account both updated and initial values
      const totalSubtotal = Object.entries(data).reduce((subtotalAcc, [sowId]) => {
        return subtotalAcc + calculateTotalAmount(sowId);
      }, 0);
    
      setSubtotal(parseFloat(totalSubtotal.toFixed(2)));
      setActualSubTotal(parseFloat(actualSubAmount.toFixed(2)));
    }, [subAmounts, updatedAmounts, setSubtotal, data]);

    useEffect(() => {
      setUpdatedAmounts({});
      setTableRows(renderTableRows());
    }, [data, visibleColumns, subAmounts]);
    
    return (
      <div ref={ref} className="w-full flex flex-col items-center mb-4">
        <div className="flex items-center mb-4">
          <label className="mr-4 font-semibold">Columns Settings:</label>
          <div className="flex gap-4 flex-wrap">
            {columnOptions.map((option) => {
              if (
                !visibleColumns.some(column => column.label === 'Service Type') &&
                ['rate', 'hours', 'subAmount'].includes(option.value)
              ) {
                return null;
              }

              return (
                <CheckboxButton
                  key={option.value}
                  variant="text"
                  className="hover:bg-slate-300 dark:hover:bg-blue rounded-none dark:bg-slate-800"
                  checked={visibleColumns.some(column => column.label === option.label)}
                  onCheckedChange={(checked) =>
                    handleColumnVisibilityChange(option.label, option.value, checked as boolean)
                  }
                >
                  {option.label}
                </CheckboxButton>
              );
            })}
          </div>
        </div>
        
        <table className="w-full border-collapse text-sm" style={{ tableLayout: "fixed" }}>
          <thead className="border-b-8 border-border">
            <tr>
              {visibleColumns.some(column => column.label === 'Sow ID') && (
                <th className="p-2 text-left min-w-36">Sow ID</th>
              )}
              {visibleColumns.some(column => column.label === 'Service Period') && (
                <th className="p-2 text-left min-w-36">Service Period</th>
              )}
              {visibleColumns.some(column => column.label === 'Service Type') && (
                <th className="p-2 text-left min-w-64">Service Type</th>
              )}
              {visibleColumns.some(column => column.label === 'Rate') && (
                <th className="p-2 text-right">Rate</th>
              )}
              {visibleColumns.some(column => column.label === 'Description') && (
                <th className="p-2 text-center">Description</th>
              )}
              {visibleColumns.some(column => column.label === 'Hours') && (
                <th className="p-2 text-right min-w-28">Monthly Hours / Output Count</th>
              )}
              {visibleColumns.some(column => column.label === 'Sub-Amount') && (
                <th className="p-2 text-right min-w-36">Sub-Amount</th>
              )}
              <th className="p-1 text-right min-w-36">Amount</th>
            </tr>
          </thead>
          <tbody>{tableRows}</tbody>
        </table>
      </div>
    );
  }
);

export default InvoiceTable;
